import { useContext, useState, useEffect } from 'react';
import { isEmpty } from 'lodash';

import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useLazyDataModel } from '@thd-nucleus/data-sources';

import { RentalEstimateContext } from '../context/RentalEstimateContext';
import { getEstimate } from '../utils/api';
import { transformEstimateData, } from '../utils/rate-helper';
import { timestampToIsoFormat } from '../utils/helper';

export const useEstimateApi = () => {
  const {
    pickupTime,
    returnTime,
    storeId,
    rentalCategory,
    rentalSubCategory,
    svocId,
    deliveryAddress,
    graphQlEnabled,
    suppliedEstimateData
  } = useContext(RentalEstimateContext);
  const [data, setData] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(isEmpty(suppliedEstimateData));

  const ctx = useContext(ExperienceContext);
  const { hosts } = ctx;
  const { apionline: host } = hosts || {};

  const variables = {
    pickupTime: timestampToIsoFormat(pickupTime),
    returnTime: timestampToIsoFormat(returnTime),
    ...(deliveryAddress
      ? {
        delivery: {
          address: {
            street: deliveryAddress?.street,
            city: deliveryAddress?.city,
            stateProvince: deliveryAddress?.state,
            postalCode: deliveryAddress?.zipCode,
            countryCode: deliveryAddress?.countryCode || 'US'
          }
        }
      }
      : { store: storeId }),
    tools: [
      {
        categoryCode: rentalCategory,
        subCategoryCode: rentalSubCategory
      }
    ],
    svocId
  };
  const [getRentalEstimateData] = useLazyDataModel('getRentalEstimate', {
    variables,
    ssr: false
  });

  const executeRequest = async () => {
    setLoading(true);
    setData({});
    try {
      let resolved = null;
      if (graphQlEnabled) {
        const { data: estimateData, error: estimateError } = await getRentalEstimateData({ variables });

        if (estimateError) {
          console.error('Error fetching rental estimate:', estimateError);
          throw new Error(estimateError);
        }

        resolved = estimateData.getRentalEstimate;
      } else {
        resolved = await getEstimate({
          host,
          pickupTime,
          returnTime,
          storeId,
          rentalCategory,
          rentalSubCategory
        });
      }
      setData(transformEstimateData(resolved));
      setError('');
    } catch (err) {
      setData({});
      setError(err.message || 'Unexpected Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!suppliedEstimateData || isEmpty(suppliedEstimateData)) {
      executeRequest();
    } else {
      setData(transformEstimateData(suppliedEstimateData));
    }
  }, [suppliedEstimateData, graphQlEnabled]);

  return {
    data,
    error,
    loading,
    executeRequest
  };
};
